<template>
  <div class="page">
    <div class="header">
      <div class="description">
        <h1 class="title">Nova Consulta em Lote</h1>
      </div>
    </div>

    <div class="card">
      <div class="body">
        <validation-observer
          ref="form"
          v-show="!finished"
        >
          <form @submit.prevent="submitForm">
            <form-wizard
              class="wizard"
              step-size="sm"
              title=""
              subtitle=""
              next-button-text="Avançar"
              back-button-text="Voltar"
              finish-button-text="Finalizar"
              :start-index.sync="step"
              @on-change="changeTab"
              ref="wizard"
            >
              <tab-content
                class="tab tab-step1"
                title="Template"
                :before-change="validateForm"
              >
                <template v-if="step === 0">
                  <error-cant-fetch v-if="errors.cantFetch"></error-cant-fetch>
                  <step-template v-model="form" :tag="tag" v-else/>
                </template>
              </tab-content>
              <tab-content
                class="tab tab-step2"
                title="Arquivo"
                :before-change="validateForm"
              >
                <template v-if="step === 1">
                  <step-file v-model="form" ref="stepFile" :tag="tag"/>
                </template>
              </tab-content>
              <tab-content
                class="tab tab-step3"
                title="Finalizar"
                :before-change="validateForm"
              >
                <template v-if="step === 2">
                  <error-cant-create
                    v-if="errors.cantCreate"
                    :unprocessable-entity="errors.unprocessableEntityWhenCreate"
                    :payment-required="errors.paymentRequiredWhenCreate"
                  />
                  <step-finish v-model="form" :tag="tag"></step-finish>
                </template>
              </tab-content>

              <template slot="footer" slot-scope="props">
                <div class="wizard-footer-buttons" v-if="!errors.cantFetch">
                  <wizard-button
                    @click.native="props.prevTab()"
                    :style="props.fillButtonStyle"
                    :disabled="props.activeTabIndex === 0"
                    class="prev"
                  >Voltar</wizard-button>
                  <wizard-button
                    v-if="!props.isLastStep"
                    @click.native="props.nextTab()"
                    :style="props.fillButtonStyle"
                    class="next"
                  >Avançar</wizard-button>
                  <wizard-button
                    v-else
                    @click.native="createBatch()"
                    :style="props.fillButtonStyle"
                    class="finish"
                  >Finalizar</wizard-button>
                </div>
              </template>
            </form-wizard>
          </form>
        </validation-observer>

        <batch-created
          v-if="finished"
          @new-batch="newBatch"
        />
        <loader v-if="loading"/>
      </div>
    </div>
  </div>
</template>

<script>
import Loader from '@/support/components/Loader.vue';
import { ValidationObserver } from 'vee-validate';
import { FormWizard, TabContent, WizardButton } from 'vue-form-wizard';
import currency from '@/support/filters/currency';
import StepTemplate from '@/batch/components/StepTemplate.vue';
import StepFile from '@/batch/components/StepFile.vue';
import StepFinish from '@/batch/components/StepFinish.vue';
import ErrorCantFetch from '@/compliance/components/new-query/messages/ErrorCantFetch.vue';
import ErrorCantCreate from '@/compliance/components/new-query/messages/ErrorCantCreate.vue';
import BatchCreated from '@/batch/components/BatchCreated.vue';
import { mapState } from 'vuex';

export default {
  name: 'BatchCreate',

  data() {
    return {
      loading: true,
      finished: false,
      step: 0,
      errors: {
        cantFetch: false,
        unprocessableEntityWhenCreate: false,
        paymentRequiredWhenCreate: false,
        cantCreate: false,
      },
      form: {
        id: '',
        consultations: [],
        template: [],
        file: null,
        recurrent: false,
        periodicity: '',
        name: '',
      },
    };
  },

  methods: {
    submitForm() {
    },

    async loadRequiredData() {
      try {
        this.resetErrors();
        this.loading = true;

        await this.$store.dispatch('compliance/fetchAllTemplates', this.tag);
      } catch {
        this.errors.cantFetch = true;
      } finally {
        this.loading = false;
      }
    },

    async fetchTemplate() {
      if (this.form.template.id && this.step === 0) {
        this.loading = true;
        await this.$store.dispatch('template/fetchTemplateDetail', this.form.template.id);
        this.loading = false;
      }
    },

    async createBatch() {
      try {
        this.loading = true;
        this.resetErrors();

        await this.$store.dispatch('batch/create', {
          template_id: this.form.template.id,
          tag: this.tag,
          label: this.form.id,
          file: this.form.file,
          recurrent: this.form.recurrent,
          periodicity: this.form.periodicity,
          name: this.form.name,
        });

        this.finished = true;
      } catch ({ response }) {
        this.errors.cantCreate = true;

        if (response.status === 422) {
          this.errors.unprocessableEntityWhenCreate = response.data;
        }

        if (response.status === 402) {
          this.errors.paymentRequiredWhenCreate = true;
        }
      } finally {
        this.loading = false;
      }
    },

    resetErrors() {
      this.errors.cantFetch = false;
      this.errors.unprocessableEntityWhenCreate = false;
      this.errors.paymentRequiredWhenCreate = false;
      this.errors.cantCreate = false;
    },

    async validateForm() {
      this.resetErrors();
      await this.fetchTemplate();
      await this.calculateTotal();
      return !!(await this.$refs.form.validate());
    },

    async calculateTotal() {
      if (this.step === 1) {
        try {
          this.loading = true;
          await this.$store.dispatch('batch/calculateTotal', {
            template_id: this.form.template.id,
            tag: this.tag,
            label: this.form.id,
            file: this.form.file,
            recurrent: this.form.recurrent,
            periodicity: this.form.periodicity,
            name: this.form.name,
          });
        } catch ({ response }) {
          this.errors.cantCreate = true;

          if (response.status === 422) {
            this.errors.unprocessableEntityWhenCreate = response.data;
          }
        } finally {
          this.loading = false;
        }
      }
    },

    newBatch() {
      this.finished = false;
      this.form = {
        id: '',
        consultations: [],
        template: [],
        file: null,
        recurrent: false,
        periodicity: '',
        name: '',
      };
      this.$refs.wizard.navigateToTab(0);
    },

    /**
     *
     * @param prevIndex
     * @param nextIndex
     */
    changeTab() {
    },
  },

  mounted() {
    this.loadRequiredData();
  },

  computed: {
    tag() {
      return this.$route.meta.tag;
    },
    ...mapState('batch', {
      template: 'total',
    }),
  },

  watch: {
    $route(to, from) {
      if (to.path.includes('batch') && from.path.includes('batch')) {
        this.newBatch();
        this.loadRequiredData();
      }
    },
  },

  filters: {
    currency,
  },

  components: {
    StepFinish,
    Loader,
    ValidationObserver,
    FormWizard,
    TabContent,
    WizardButton,
    StepTemplate,
    StepFile,
    ErrorCantFetch,
    ErrorCantCreate,
    BatchCreated,
  },
};
</script>

<style lang="scss" scoped>
.wizard {
  .wizard-footer-header {
    @apply text-black;
    @apply text-xs;
    @apply italic;
    @apply mt-2;
  }

  .tab {
    &:not(.tab--only-show-error) {
      padding-bottom: 2em;
    }
  }

  .wizard-footer-buttons {
    width: 100%!important;
  }
}
</style>
